import { MagistralDrug } from '@dreryk/drerykweb-medicine-api';
import { MedicinePrescription, MedicinePrescriptionQuery } from '@dreryk/egabinet-prescription-frontend-api';

import { useCqrsGet } from '@dreryk/command-query-react-common';
import { commonTranslationKeys } from '@dreryk/react-common';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Choice, Slot } from '@dreryk/watermelon';

import { useGetPatientEntitlementToMedicalServiceStatus } from '~libs.shared.patient-entitlement-to-medical-service';
import { VisitId } from '~libs.shared.visit';

import { MagistralDrugHeading } from '../../../magistralDrug/MagistralDrugHeading';
import { MagistralDrugSubheading } from '../../../magistralDrug/MagistralDrugSubheading';
import { PatientId } from '../../../PrescriptionIssuingPanel.types';
import { Quantity } from '../../prescription/quantity/quantity.types';
import { DraftPrescriptionCardSkeleton } from '../common/DraftPrescriptionCard.skeleton';

type DraftMagistralDrugPrescriptionFieldLoaderRenderProps = {
   medicinePrescription: MedicinePrescription;
   medicinePrescriptionFetching: boolean;
};

type DraftMagistralDrugPrescriptionFieldLoaderProps = {
   readonly patientId: PatientId;
   readonly visitId: VisitId;
   readonly magistralDrugId: MagistralDrug['id'];
   readonly initialData: MedicinePrescription | undefined;
   readonly medicinePackageQuantity: Quantity;
   readonly magistralDrug: MagistralDrug | undefined;
   readonly index: number;
   readonly children: (renderProps: DraftMagistralDrugPrescriptionFieldLoaderRenderProps) => JSX.Element;
};

export const DraftMagistralDrugPrescriptionFieldLoader = (
   props: DraftMagistralDrugPrescriptionFieldLoaderProps
): JSX.Element => {
   const patientEntitlementToMedicalServiceStatus = useGetPatientEntitlementToMedicalServiceStatus(props.visitId);

   const {
      data: medicinePrescription,
      loading: medicinePrescriptionLoading,
      error: medicinePrescriptionError,
      fetching: medicinePrescriptionFetching,
   } = useCqrsGet<MedicinePrescription>(
      () => {
         const query = new MedicinePrescriptionQuery();
         query.patientId = props.patientId;
         query.magistralDrugId = Slot.of(props.magistralDrugId!);
         query.ewusStatus = Choice.withUseDefaultIfNull(patientEntitlementToMedicalServiceStatus.ewusStatus);
         query.medicinePackageQuantity = Choice.of(props.medicinePackageQuantity);
         return query;
      },
      {
         initialData: props.initialData,
      }
   );

   if (medicinePrescriptionLoading) {
      return props.magistralDrug ? (
         <DraftPrescriptionCardSkeleton
            testId={props.index}
            heading={
               <MagistralDrugHeading
                  name={props.magistralDrug.name}
                  magistralDrugMedicineRawMaterials={props.magistralDrug.magistralDrugMedicineRawMaterials}
               />
            }
            subheading={
               <MagistralDrugSubheading
                  name={props.magistralDrug.name}
                  magistralDrugMedicineRawMaterials={props.magistralDrug.magistralDrugMedicineRawMaterials}
                  additionalDetails={props.magistralDrug.additionalDetails}
                  form={props.magistralDrug.form}
               />
            }
         />
      ) : (
         <LoaderSpinner />
      );
   }

   if (medicinePrescriptionError) {
      return (
         <Message
            testId={'fetchError'}
            type={MessageType.NEGATIVE}
            label={commonTranslationKeys.unexpectedErrorHasOccurred}
            displayIndicator
         />
      );
   }

   return props.children({
      medicinePrescription: medicinePrescription,
      medicinePrescriptionFetching: medicinePrescriptionFetching,
   });
};
